import React, { useEffect, useState, memo } from 'react';
import axios from 'axios';
import styles from './FilesItem.module.css';

/* ===== helpers ===== */
const EXT_BY_MIME = {
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/webp': '.webp',
  'application/pdf': '.pdf',
  'audio/wav': '.wav',
  'audio/x-wav': '.wav',
  'application/zip': '.zip',
  'application/x-zip-compressed': '.zip',
};

function filenameFromContentDisposition(cd) {
  if (!cd) return null;
  try {
    const star = /filename\*\s*=\s*([^']*)''([^;]+)/i.exec(cd);
    if (star && star[2]) return decodeURIComponent(star[2].trim());
    const normal = /filename\s*=\s*"?([^";]+)"?/i.exec(cd);
    if (normal && normal[1]) return normal[1].trim();
  } catch (_) {}
  return null;
}
function extFromUrl(url) {
  try {
    const u = new URL(url, window.location.origin);
    const m = u.pathname.toLowerCase().match(/\.(png|jpg|jpeg|webp|pdf|wav|zip)$/i);
    return m ? `.${m[1].toLowerCase()}` : null;
  } catch (_) { return null; }
}
async function headWithAuth(url, token) {
  const sep = url.includes('?') ? '&' : '?';
  const bust = `${sep}t=${Date.now()}`;
  return axios.head(url + bust, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
}
async function fetchBlobWithAuth(url, token, onProgress) {
  const sep = url.includes('?') ? '&' : '?';
  const bust = `${sep}t=${Date.now()}`;
  return axios.get(url + bust, {
    responseType: 'blob',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    onDownloadProgress: onProgress,
  });
}
function pickExtension(contentType, fallbackUrl) {
  if (contentType) {
    const ext = EXT_BY_MIME[contentType.toLowerCase()];
    if (ext) return ext;
  }
  return extFromUrl(fallbackUrl) || '.bin';
}
function safeDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
async function waitForAvailability(url, maxMs = 120000) {
  const token = localStorage.getItem('authToken');
  const start = Date.now();
  let delay = 800;
  while (Date.now() - start < maxMs) {
    try {
      const r = await headWithAuth(url, token);
      if (r?.status >= 200 && r?.status < 400) return true;
    } catch (_) {}
    await sleep(delay);
    delay = Math.min(Math.floor(delay * 1.6), 7000);
  }
  return false;
}

/* ===== компонент ===== */
const TicketItem = ({
  id,
  orderId,                 // для генерації
  coverImage,
  title,
  genre,
  artist,
  specialGuests = [],
  supportActs = [],
  city,
  venue,
  date,
  timeStart,
  artistSticker,

  // квиткові/завантаження (із бекенду)
  ticketGenerated,
  ticketUrl,
  downloadUrl,
  purchasedPre,
  purchasedFull,

  // інтеракції зі списком
  onTagClick = () => {},
  selectedTags = [],
  isAuthenticated,
}) => {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(null);

  // квиток: локальний стан
  const [localTicketGenerated, setLocalTicketGenerated] = useState(!!ticketGenerated);
  const [localTicketUrl, setLocalTicketUrl] = useState(ticketUrl || '');
  const [isTicketAvailable, setIsTicketAvailable] = useState(false);
  const [ticketGenerating, setTicketGenerating] = useState(false);

  // ім’я для квитка (збереження в LS)
  const [fullName, setFullName] = useState(() => localStorage.getItem('ticketFullName') || '');
  useEffect(() => localStorage.setItem('ticketFullName', fullName), [fullName]);

  // resize
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // автозакриття повідомлень
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => { setMessage(null); setMessageType(null); }, 3500);
    return () => clearTimeout(t);
  }, [message]);

  // синхронізація пропсів
  useEffect(() => setLocalTicketGenerated(!!ticketGenerated), [ticketGenerated]);
  useEffect(() => setLocalTicketUrl(ticketUrl || ''), [ticketUrl]);

  // разова перевірка доступності PDF
  useEffect(() => {
    (async () => {
      if (!localTicketGenerated || !localTicketUrl) {
        setIsTicketAvailable(false);
        return;
      }
      try {
        const token = localStorage.getItem('authToken');
        const r = await headWithAuth(localTicketUrl, token);
        setIsTicketAvailable(r?.status >= 200 && r?.status < 400);
      } catch {
        setIsTicketAvailable(false);
      }
    })();
  }, [localTicketGenerated, localTicketUrl]);

  // розумне завантаження
  const handleSmartDownload = async (link, fallbackBaseName, expectedExt) => {
    if (!link) {
      setMessage('Посилання на завантаження відсутнє.');
      setMessageType('error');
      return;
    }
    try {
      setDownloadProgress(0);
      const token = localStorage.getItem('authToken');

      let contentType = null;
      let contentDisp = null;
      try {
        const head = await headWithAuth(link, token);
        contentType = head?.headers?.['content-type'] || head?.headers?.['Content-Type'];
        contentDisp = head?.headers?.['content-disposition'] || head?.headers?.['Content-Disposition'];
      } catch (_) {}

      const resp = await fetchBlobWithAuth(link, token, (e) => {
        if (e?.total) {
          const pct = Math.round((e.loaded * 100) / e.total);
          setDownloadProgress(pct);
        }
      });

      const mime = (resp?.headers?.['content-type'] || contentType || 'application/octet-stream').toLowerCase();
      const nameFromCD = filenameFromContentDisposition(resp?.headers?.['content-disposition'] || contentDisp);

      let ext = nameFromCD ? null : pickExtension(mime, link);
      if (!nameFromCD && expectedExt && ext === '.bin') ext = expectedExt;

      const base = (fallbackBaseName || 'download').replace(/[^\w-]+/g, '_');
      const filename = nameFromCD || (base + ext);

      const blob = new Blob([resp.data], { type: mime });
      safeDownload(blob, filename);
    } catch {
      setMessage('Готуємо ваш файл, поверніться трохи згодом!');
      setMessageType('error');
    } finally {
      setDownloadProgress(null);
    }
  };

  // кнопки завантаження
  const handleDownload = async (type) => {
    if (type === 'ticket') {
      return handleSmartDownload(localTicketUrl, `${title || 'ticket'}-ticket`, '.pdf');
    }
    if (type === 'pre') {
      if (!purchasedPre) {
        setMessage('Квиток(L1) недоступний для цього замовлення.');
        setMessageType('error');
        return;
      }
      return handleSmartDownload(downloadUrl, `${title || 'pre'}-pre`, '.png');
    }
    if (type === 'full') {
      if (!purchasedFull) {
        setMessage('Квиток(L2) недоступний для цього замовлення.');
        setMessageType('error');
        return;
      }
      return handleSmartDownload(downloadUrl, `${title || 'full'}-full`, '.png');
    }
  };

  // генерація квитка
  const handleGenerateTicket = async () => {
    if (!isAuthenticated) {
      setMessage('Авторизуйтесь.');
      setMessageType('error');
      return;
    }
    if (!orderId) {
      setMessage('Немає orderId для генерації.');
      setMessageType('error');
      return;
    }
    if (!fullName.trim()) {
      setMessage('Вкажіть ім’я та прізвище.');
      setMessageType('error');
      return;
    }

    try {
      setTicketGenerating(true);
      setMessage('Генеруємо квиток…');
      setMessageType('success');

      const token = localStorage.getItem('authToken');
      const { data } = await axios.post(
        '/api/tickets/generate-ticket',
        { orderId, fullName: fullName.trim() },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      const url = data?.ticketUrl;
      if (!url) {
        setMessage('Не отримали посилання на квиток.');
        setMessageType('error');
        return;
      }

      setLocalTicketGenerated(true);
      setLocalTicketUrl(url);
      setIsTicketAvailable(false);

      const ok = await waitForAvailability(url, 120000);
      setIsTicketAvailable(!!ok);

      if (!ok) {
        setMessage('Файл готується. Спробуйте трохи згодом.');
        setMessageType('error');
      }
    } catch (e) {
      const apiMsg = e?.response?.data?.error || e?.response?.data?.message;
      setMessage(apiMsg || 'Помилка генерації.');
      setMessageType('error');
    } finally {
      setTicketGenerating(false);
    }
  };

  return (
    <div id={`show-${id}`} className={styles.filesItem}>
      {/* повідомлення */}
      {message && (
        <div className={styles.overlay}>
          <div className={`${styles.overlayMessage} ${styles[messageType]}`}>
            {message}
          </div>
        </div>
      )}

      {/* Кавер + стікер (як у Show) */}
      {isMobile ? (
        <div className={styles.coverContainer}>
          <img
            src={coverImage}
            alt={`${title} cover`}
            className={styles.filesItemCoverMobile}
            loading="lazy"
          />
        </div>
      ) : (
        <div className={styles.filesCover}>
          <img
            src={coverImage}
            alt={`${title} cover`}
            className={styles.filesItemCover}
            loading="lazy"
          />
          {artistSticker && (
            <img
              src={artistSticker}
              alt="Artist Sticker"
              className={`${styles.artistSticker} ${styles.artistStickerDesktop}`}
              onClick={() => onTagClick(artistSticker)}
            />
          )}
        </div>
      )}

      {/* Інфо (як у Show) */}
      <div className={styles.filesInfo}>
        <h3 className={styles.filesTitle}>{title}</h3>
        <p className={styles.filesGenre}>{genre}</p>

        <div className={styles.filesDetails}>
          {artist && (
            <span className={styles.filesLine}>
              <strong>Хедлайнер:</strong> {artist}
            </span>
          )}
          {(specialGuests.length > 0 || supportActs.length > 0) && (
            <span className={styles.filesLine}>
              {specialGuests.length > 0 && <span><strong>Спец-гості:</strong> {specialGuests.join(', ')}</span>}
              {specialGuests.length > 0 && supportActs.length > 0 && <span> · </span>}
              {supportActs.length > 0 && <span><strong>Розігрів:</strong> {supportActs.join(', ')}</span>}
            </span>
          )}
          <span className={styles.filesLine}>
            <strong>Дата та час:</strong> {date}{timeStart ? ` · ${timeStart}` : ''}
          </span>
          <span className={styles.filesLine}>
            <strong>Локація:</strong> {city}{venue ? `, ${venue}` : ''}
          </span>
        </div>

        {/* Теги — як у Show: жанр + місто */}
        <div className={styles.filesTags}>
          {genre && (
            <button
              className={`${styles.filesTag} ${selectedTags.includes(genre) ? styles.activefilesTag : ''}`}
              onClick={() => onTagClick(genre)}
            >
              {genre}
            </button>
          )}
          {city && (
            <button
              className={`${styles.filesTag} ${selectedTags.includes(city) ? styles.activefilesTag : ''}`}
              onClick={() => onTagClick(city)}
            >
              {city}
            </button>
          )}
        </div>
      </div>

      {/* Для мобіли — стікер окремо */}
      {isMobile && artistSticker && (
        <img
          src={artistSticker}
          alt="Artist Sticker"
          className={`${styles.artistSticker} ${styles.artistStickerMobile}`}
          onClick={() => onTagClick(artistSticker)}
        />
      )}

      {/* Генерація квитка (до появи PDF) */}
      {!localTicketGenerated && (
        <div className={styles.filesActions}>
          <div className={styles.inputContainer}>
            <input
              type="text"
              placeholder="Повне ім’я"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className={styles.inputField}
              autoComplete="name"
            />
          </div>
          <button
            className={styles.generateButton}
            onClick={handleGenerateTicket}
            disabled={!isAuthenticated || ticketGenerating}
          >
            {ticketGenerating ? 'Генерація…' : 'Згенерувати Квиток'}
          </button>
        </div>
      )}

      {/* Дії (кнопки завантаження) */}
      <div className={styles.filesActions}>
        {purchasedPre && (
          <button
            className={styles.downloadButton}
            onClick={() => handleDownload('pre')}
            disabled={!isAuthenticated}
          >
            Press Kit(L1)
          </button>
        )}

        {purchasedFull && (
          <button
            className={styles.downloadButton}
            onClick={() => handleDownload('full')}
            disabled={!isAuthenticated}
          >
            Press Kit(L2)
          </button>
        )}

        {localTicketGenerated && (
          <button
            className={styles.downloadButton}
            onClick={() => handleDownload('ticket')}
            disabled={!isAuthenticated || !isTicketAvailable}
          >
            {isTicketAvailable ? 'Мій Квиток' : 'Готуємо...'}
          </button>
        )}
      </div>

      {/* прогрес завантаження */}
      {downloadProgress !== null && (
        <div className={styles.progressOverlay}>
          <div className={styles.progressText}>{downloadProgress}%</div>
        </div>
      )}
    </div>
  );
};

export default memo(TicketItem);

